import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { LenderRiskBadge } from "@/components/lenders/LenderRiskBadge";
import { useToast } from "@/components/ui/use-toast";
import { ArrowLeft, Mail, Phone, MessageSquare, AlertTriangle, User, CreditCard, PiggyBank, FileText } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

const mockBorrowers = [
  {
    id: "1",
    name: "John Smith",
    email: "john.smith@example.com",
    phone: "+1 (555) 123-4567",
    avatar: "",
    credit_score: "745",
    risk_level: "conservative",
    active_loans: "2",
    total_borrowed: "150000",
    status: "active",
    address: "123 Main St, New York, NY 10001",
    occupation: "Software Engineer",
    employer: "Tech Solutions Inc.",
    income: "120000",
    loan_history: [
      { id: "loan1", amount: "75000", date: "2023-01-15", status: "active", purpose: "Mortgage" },
      { id: "loan2", amount: "25000", date: "2023-06-22", status: "active", purpose: "Auto Loan" },
      { id: "loan3", amount: "50000", date: "2022-03-10", status: "completed", purpose: "Home Renovation" }
    ],
    documents: [
      { id: "doc1", name: "Income Verification", date: "2023-01-05", status: "verified" },
      { id: "doc2", name: "Credit Report", date: "2023-01-05", status: "verified" },
      { id: "doc3", name: "Identity Verification", date: "2023-01-05", status: "verified" }
    ]
  },
  {
    id: "2",
    name: "Sarah Johnson",
    email: "sarah.j@example.com",
    phone: "+1 (555) 987-6543",
    avatar: "",
    credit_score: "680",
    risk_level: "moderate",
    active_loans: "1",
    total_borrowed: "75000",
    status: "active",
    address: "456 Park Ave, Boston, MA 02108",
    occupation: "Marketing Manager",
    employer: "Global Marketing Group",
    income: "95000",
    loan_history: [
      { id: "loan1", amount: "75000", date: "2023-03-20", status: "active", purpose: "Mortgage" }
    ],
    documents: [
      { id: "doc1", name: "Income Verification", date: "2023-03-10", status: "verified" },
      { id: "doc2", name: "Credit Report", date: "2023-03-10", status: "verified" },
      { id: "doc3", name: "Identity Verification", date: "2023-03-10", status: "verified" }
    ]
  },
  {
    id: "3",
    name: "Michael Davis",
    email: "michael.d@example.com",
    phone: "+1 (555) 456-7890",
    avatar: "",
    credit_score: "620",
    risk_level: "aggressive",
    active_loans: "3",
    total_borrowed: "85000",
    status: "flagged",
    address: "789 Oak St, Chicago, IL 60601",
    occupation: "Sales Representative",
    employer: "Midwest Retail Solutions",
    income: "70000",
    loan_history: [
      { id: "loan1", amount: "30000", date: "2023-02-15", status: "active", purpose: "Auto Loan" },
      { id: "loan2", amount: "35000", date: "2023-04-10", status: "active", purpose: "Debt Consolidation" },
      { id: "loan3", amount: "20000", date: "2023-06-25", status: "active", purpose: "Personal Loan" }
    ],
    documents: [
      { id: "doc1", name: "Income Verification", date: "2023-02-05", status: "verified" },
      { id: "doc2", name: "Credit Report", date: "2023-02-05", status: "flagged" },
      { id: "doc3", name: "Identity Verification", date: "2023-02-05", status: "verified" }
    ]
  },
  {
    id: "4",
    name: "Emma Wilson",
    email: "emma.w@example.com",
    phone: "+1 (555) 789-0123",
    avatar: "",
    credit_score: "790",
    risk_level: "conservative",
    active_loans: "1",
    total_borrowed: "200000",
    status: "active",
    address: "101 Pine St, San Francisco, CA 94101",
    occupation: "Doctor",
    employer: "City Medical Center",
    income: "250000",
    loan_history: [
      { id: "loan1", amount: "200000", date: "2023-01-30", status: "active", purpose: "Mortgage" }
    ],
    documents: [
      { id: "doc1", name: "Income Verification", date: "2023-01-15", status: "verified" },
      { id: "doc2", name: "Credit Report", date: "2023-01-15", status: "verified" },
      { id: "doc3", name: "Identity Verification", date: "2023-01-15", status: "verified" }
    ]
  },
  {
    id: "5",
    name: "Robert Brown",
    email: "robert.b@example.com",
    phone: "+1 (555) 234-5678",
    avatar: "",
    credit_score: "710",
    risk_level: "moderate",
    active_loans: "2",
    total_borrowed: "120000",
    status: "active",
    address: "222 Maple Ave, Seattle, WA 98101",
    occupation: "IT Manager",
    employer: "Northwest Tech",
    income: "110000",
    loan_history: [
      { id: "loan1", amount: "100000", date: "2022-11-15", status: "active", purpose: "Mortgage" },
      { id: "loan2", amount: "20000", date: "2023-05-10", status: "active", purpose: "Home Improvement" }
    ],
    documents: [
      { id: "doc1", name: "Income Verification", date: "2022-11-01", status: "verified" },
      { id: "doc2", name: "Credit Report", date: "2022-11-01", status: "verified" },
      { id: "doc3", name: "Identity Verification", date: "2022-11-01", status: "verified" }
    ]
  },
  {
    id: "6",
    name: "Lisa Anderson",
    email: "lisa.a@example.com",
    phone: "+1 (555) 345-6789",
    avatar: "",
    credit_score: "550",
    risk_level: "aggressive",
    active_loans: "1",
    total_borrowed: "30000",
    status: "inactive",
    address: "333 Cedar St, Miami, FL 33101",
    occupation: "Freelancer",
    employer: "Self-employed",
    income: "45000",
    loan_history: [
      { id: "loan1", amount: "30000", date: "2022-08-20", status: "inactive", purpose: "Business Loan" }
    ],
    documents: [
      { id: "doc1", name: "Income Verification", date: "2022-08-10", status: "verified" },
      { id: "doc2", name: "Credit Report", date: "2022-08-10", status: "verified" },
      { id: "doc3", name: "Identity Verification", date: "2022-08-10", status: "verified" }
    ]
  },
  {
    id: "7",
    name: "David Miller",
    email: "david.m@example.com",
    phone: "+1 (555) 567-8901",
    avatar: "",
    credit_score: "650",
    risk_level: "moderate",
    active_loans: "0",
    total_borrowed: "0",
    status: "pending",
    address: "444 Birch St, Denver, CO 80201",
    occupation: "Accountant",
    employer: "Mountain Financial Services",
    income: "85000",
    loan_history: [],
    documents: [
      { id: "doc1", name: "Income Verification", date: "2023-07-25", status: "pending" },
      { id: "doc2", name: "Credit Report", date: "2023-07-25", status: "pending" },
      { id: "doc3", name: "Identity Verification", date: "2023-07-25", status: "verified" }
    ]
  }
];

const BorrowerStatusBadge = ({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 hover:bg-green-100";
      case "pending":
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
      case "inactive":
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
      case "flagged":
        return "bg-red-100 text-red-800 hover:bg-red-100";
      case "completed":
        return "bg-blue-100 text-blue-800 hover:bg-blue-100";
      case "verified":
        return "bg-green-100 text-green-800 hover:bg-green-100";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
    }
  };

  return (
    <Badge className={getStatusColor()} variant="outline">
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

const BorrowerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");

  const { data: borrower, isLoading } = useQuery({
    queryKey: ['borrower', id],
    queryFn: async () => {
      const found = mockBorrowers.find(b => b.id === id);
      if (!found) {
        throw new Error("Borrower not found");
      }
      return found;
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading borrower details...</p>
        </div>
      </div>
    );
  }

  if (!borrower) {
    return (
      <div className="space-y-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/lender/borrowers")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Borrowers
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10">
            <AlertTriangle className="h-10 w-10 text-amber-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Borrower Not Found</h2>
            <p className="text-muted-foreground text-center">
              The borrower you're looking for couldn't be found or you don't have permission to view their details.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getCreditScoreColor = (score) => {
    const parsedScore = parseInt(score);
    if (parsedScore >= 740) return "text-green-600";
    else if (parsedScore >= 670) return "text-green-500";
    else if (parsedScore >= 580) return "text-amber-500";
    else return "text-red-500";
  };

  const getCreditScoreProgress = (score) => {
    return (parseInt(score) / 850) * 100;
  };

  const handleContact = (method) => {
    toast({
      title: "Contact Initiated",
      description: `${method === 'email' ? 'Email' : 'Call'} to ${borrower.name} has been initiated.`,
    });
  };

  const handleFlagBorrower = () => {
    toast({
      title: "Borrower Flagged",
      description: `${borrower.name} has been flagged for review.`,
      variant: "destructive",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/lender/borrowers")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Borrowers
        </Button>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleContact('email')}
            className="flex items-center gap-1"
          >
            <Mail className="h-4 w-4" />
            Email
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleContact('call')}
            className="flex items-center gap-1"
          >
            <Phone className="h-4 w-4" />
            Call
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => toast({
              title: "Message Sent",
              description: `Message to ${borrower.name} has been sent.`,
            })}
            className="flex items-center gap-1"
          >
            <MessageSquare className="h-4 w-4" />
            Message
          </Button>
          {borrower.status !== "flagged" && (
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={handleFlagBorrower}
              className="flex items-center gap-1"
            >
              <AlertTriangle className="h-4 w-4" />
              Flag
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-3">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
              <Avatar className="h-20 w-20">
                <AvatarImage src={borrower.avatar} alt={borrower.name} />
                <AvatarFallback className="text-xl">
                  {borrower.name.split(" ").map(n => n[0]).join("")}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1 flex-1">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <h2 className="text-2xl font-bold">{borrower.name}</h2>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span>{borrower.email}</span>
                      <span>•</span>
                      <span>{borrower.phone}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <BorrowerStatusBadge status={borrower.status} />
                    <LenderRiskBadge risk={borrower.risk_level} />
                  </div>
                </div>
                <div className="pt-2">
                  <p className="text-sm text-muted-foreground">{borrower.address}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <div>
                      <p className="text-sm font-medium">Occupation</p>
                      <p className="text-sm text-muted-foreground">{borrower.occupation}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Employer</p>
                      <p className="text-sm text-muted-foreground">{borrower.employer}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Annual Income</p>
                      <p className="text-sm text-muted-foreground">
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                          maximumFractionDigits: 0,
                        }).format(parseInt(borrower.income))}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>
                <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab}>
                  <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="loans">Loans</TabsTrigger>
                    <TabsTrigger value="documents">Documents</TabsTrigger>
                  </TabsList>
                
                  <TabsContent value="overview" className="pt-4 space-y-6">
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-medium mb-2">Credit Score</h3>
                        <div className="bg-muted p-4 rounded-md">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Current Score</span>
                            <span className={`text-xl font-bold ${getCreditScoreColor(borrower.credit_score)}`}>
                              {borrower.credit_score}
                            </span>
                          </div>
                          <Progress value={getCreditScoreProgress(borrower.credit_score)} className="h-2 mb-1" />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Poor (300)</span>
                            <span>Fair (580)</span>
                            <span>Good (670)</span>
                            <span>Excellent (740)</span>
                            <span>850</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-medium mb-2">Loan Summary</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Card>
                            <CardContent className="pt-6">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground mb-1">Active Loans</p>
                                <p className="text-3xl font-bold">{borrower.active_loans}</p>
                              </div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="pt-6">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground mb-1">Total Borrowed</p>
                                <p className="text-3xl font-bold">
                                  {new Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                    maximumFractionDigits: 0,
                                  }).format(parseInt(borrower.total_borrowed))}
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="pt-6">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground mb-1">Risk Level</p>
                                <div className="flex justify-center mt-1">
                                  <LenderRiskBadge risk={borrower.risk_level} />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="loans" className="pt-4">
                    {borrower.loan_history.length === 0 ? (
                      <div className="text-center py-10">
                        <PiggyBank className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-medium">No Loans Yet</h3>
                        <p className="text-sm text-muted-foreground">
                          This borrower doesn't have any loan history with us.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {borrower.loan_history.map((loan) => (
                          <Card key={loan.id}>
                            <CardContent className="p-4">
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-medium">{loan.purpose}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {new Date(loan.date).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                    })}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-medium">
                                    {new Intl.NumberFormat("en-US", {
                                      style: "currency",
                                      currency: "USD",
                                      maximumFractionDigits: 0,
                                    }).format(parseInt(loan.amount))}
                                  </p>
                                  <BorrowerStatusBadge status={loan.status} />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="documents" className="pt-4">
                    <div className="space-y-4">
                      {borrower.documents.map((doc) => (
                        <Card key={doc.id}>
                          <CardContent className="p-4">
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-3">
                                <FileText className="h-5 w-5 text-muted-foreground" />
                                <div>
                                  <p className="font-medium">{doc.name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {new Date(doc.date).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                    })}
                                  </p>
                                </div>
                              </div>
                              <BorrowerStatusBadge status={doc.status} />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Content is now inside TabsContent components */}
            </CardContent>
          </Card>
        </div>

        <div>
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Contact Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{borrower.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{borrower.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{borrower.phone}</span>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="space-y-2">
                  <Button 
                    className="w-full justify-start" 
                    variant="outline"
                    onClick={() => handleContact('email')}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Send Email
                  </Button>
                  <Button 
                    className="w-full justify-start" 
                    variant="outline"
                    onClick={() => handleContact('call')}
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    Call Borrower
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Financial Info</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Annual Income</span>
                    <span className="font-medium">
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        maximumFractionDigits: 0,
                      }).format(parseInt(borrower.income))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Credit Score</span>
                    <span className={`font-medium ${getCreditScoreColor(borrower.credit_score)}`}>
                      {borrower.credit_score}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Risk Level</span>
                    <LenderRiskBadge risk={borrower.risk_level} />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Button 
                    className="w-full" 
                    onClick={() => toast({
                      title: "Review Started",
                      description: `Starting comprehensive review for ${borrower.name}.`,
                    })}
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    Start Credit Review
                  </Button>
                  {borrower.status !== "flagged" ? (
                    <Button 
                      className="w-full" 
                      variant="destructive"
                      onClick={handleFlagBorrower}
                    >
                      <AlertTriangle className="mr-2 h-4 w-4" />
                      Flag Account
                    </Button>
                  ) : (
                    <Button 
                      className="w-full" 
                      variant="outline"
                      onClick={() => toast({
                        title: "Flag Removed",
                        description: `Flag has been removed from ${borrower.name}.`,
                      })}
                    >
                      <AlertTriangle className="mr-2 h-4 w-4" />
                      Remove Flag
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BorrowerDetails;
